import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Eye, Heart, MessageCircle, Share2, Users, TrendingUp, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, sub, testid, accent = "text-primary" }) => (
  <div
    data-testid={testid}
    className="rounded-xl border border-border bg-card p-4 flex flex-col gap-1"
  >
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Icon className={`w-4 h-4 ${accent}`} />
      <span>{label}</span>
    </div>
    <div className="text-2xl font-bold tracking-tight">{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
  </div>
);

function formatDay(dateStr) {
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit" });
  } catch { return dateStr; }
}

export default function PostAnalyticsDialog({ open, onOpenChange, postId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !postId) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/posts/${postId}/analytics?days=7`);
        if (!cancel) setData(r.data);
      } catch (e) {
        if (!cancel) toast.error(e.response?.data?.detail || "Não foi possível carregar as métricas");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, postId]);

  const series = data?.daily_series || [];
  const maxVal = Math.max(1, ...series.map((s) => Math.max(s.views, s.shares)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid={`post-analytics-dialog-${postId}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Métricas do post
          </DialogTitle>
          <DialogDescription>
            Veja impressões, alcance único e engajamento dos últimos 7 dias.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-10" data-testid="post-analytics-loading">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && data && (
          <div className="space-y-5">
            {/* Metric grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatCard
                icon={Eye}
                label="Impressões"
                value={data.views}
                sub={`${data.unique_viewers} pessoas únicas`}
                testid="post-analytics-views"
              />
              <StatCard
                icon={Users}
                label="Alcance único"
                value={data.unique_viewers}
                sub="visualizações distintas"
                testid="post-analytics-unique"
                accent="text-blue-500"
              />
              <StatCard
                icon={Heart}
                label="Curtidas"
                value={data.likes_count}
                sub={`+ ${data.reactions_total} reações`}
                testid="post-analytics-likes"
                accent="text-red-500"
              />
              <StatCard
                icon={MessageCircle}
                label="Comentários"
                value={data.comments_count}
                sub="incluindo respostas"
                testid="post-analytics-comments"
                accent="text-emerald-500"
              />
              <StatCard
                icon={Share2}
                label="Compartilhamentos"
                value={data.share_count}
                sub={`${data.unique_sharers} pessoas`}
                testid="post-analytics-shares"
                accent="text-purple-500"
              />
              <StatCard
                icon={TrendingUp}
                label="Engajamento"
                value={`${data.engagement_rate}%`}
                sub={`${data.interactions} interações totais`}
                testid="post-analytics-engagement"
                accent="text-orange-500"
              />
            </div>

            {/* Reactions breakdown */}
            {data.reactions_total > 0 && (
              <div data-testid="post-analytics-reactions-breakdown" className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs text-muted-foreground mb-2">Reações por emoji</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(data.reactions).map(([emoji, n]) => (
                    <div key={emoji} className="px-3 py-1 rounded-full bg-muted text-sm flex items-center gap-1">
                      <span className="text-base">{emoji}</span>
                      <span className="font-semibold">{n}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Daily bar chart */}
            <div className="rounded-xl border border-border bg-card p-4" data-testid="post-analytics-daily-chart">
              <div className="text-xs text-muted-foreground mb-3">Últimos {data.days} dias</div>
              <div className="flex items-end justify-between gap-2 h-32">
                {series.map((s) => (
                  <div key={s.date} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex items-end justify-center gap-0.5 h-24">
                      <div
                        title={`${s.views} views`}
                        style={{ height: `${(s.views / maxVal) * 100}%` }}
                        className="w-1/2 bg-primary rounded-t min-h-[2px] transition-all"
                      />
                      <div
                        title={`${s.shares} shares`}
                        style={{ height: `${(s.shares / maxVal) * 100}%` }}
                        className="w-1/2 bg-purple-500 rounded-t min-h-[2px] transition-all"
                      />
                    </div>
                    <div className="text-[10px] text-muted-foreground">{formatDay(s.date)}</div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 mt-3 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-primary rounded-sm" /> Views</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-purple-500 rounded-sm" /> Shares</span>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
